import { useEvent } from '@/utils/eventBus';
import { cn } from '@/lib/utils';
import { AlertOctagon, Trash, X } from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';
import { cloneElement, ReactNode, useEffect, useRef, useState } from 'react';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { RequestCountdown } from '../RequestCountdown/RequestCountdown';
import { useLingui, Trans } from '@lingui/react/macro';

export type ConfirmAlertTestIds = {
	dialog?: string;
	confirm?: string;
	cancel?: string;
};

export type BaseConfirmAlertProps = {
	title: string;
	description: string | ReactNode;
	onConfirm: () => void;
	onCancel: () => void;
	confirmButtonText?: string;
	cancelButtonText?: string;
	confirmButton?: ReactNode;
	cancelButton?: ReactNode;
	type?: 'warning' | 'delete' | 'info' | 'destructive';
	/** Render a single Close button instead of Confirm + Cancel. Useful for
	 *  acknowledgement-only alerts where both buttons would do the same thing. */
	hideCancelButton?: boolean;
	/** Overrides for the dialog and its two buttons, so a flow-specific test can find them. */
	testIds?: ConfirmAlertTestIds;
	/** Absolute time (ms epoch) at which the alert closes itself and reports a timeout. */
	expiresAt?: number;
	onTimeout?: () => void;
};

export const ConfirmAlert = ({
	title,
	description,
	confirmButton,
	cancelButton,
	confirmButtonText,
	cancelButtonText,
	onConfirm,
	onCancel,
	type = 'info',
	hideCancelButton = false,
	testIds,
	expiresAt,
	onTimeout,
}: BaseConfirmAlertProps) => {
	const [isOpen, setIsOpen] = useState(true);
	const { t } = useLingui();
	const onTimeoutRef = useRef(onTimeout);
	onTimeoutRef.current = onTimeout;

	useEffect(() => {
		if (expiresAt === undefined) return;
		const timer = setTimeout(
			() => {
				setIsOpen(false);
				onTimeoutRef.current?.();
			},
			Math.max(0, expiresAt - Date.now()),
		);
		return () => clearTimeout(timer);
	}, [expiresAt]);

	const overrrideProps = (comp: any, type: 'cancel' | 'confirm') => {
		if (!comp) return null;
		return cloneElement(comp, {
			onClick: () => {
				comp.props.onClick?.();
				setIsOpen(false);
				if (type === 'cancel') {
					onCancel();
				} else {
					onConfirm();
				}
			},
		});
	};

	const renderConfirmButtonVariant = () => {
		switch (type) {
			case 'warning':
				return 'secondary';
			case 'delete':
			case 'destructive':
				return 'destructive';
			default:
				return 'default';
		}
	};

	const isDestructiveStyled = type === 'destructive';

	const cancelButtonActual = overrrideProps(cancelButton, 'cancel') ?? (
		<Button
			variant="outline"
			size="lg"
			className="h-auto min-h-9 w-full whitespace-normal"
			data-testid={testIds?.cancel ?? 'confirm-alert-cancel'}
			onClick={() => {
				onCancel();
				setIsOpen(false);
			}}
		>
			{cancelButtonText ?? (
				<>
					<Trans>No</Trans>, <Trans>cancel</Trans>
				</>
			)}
		</Button>
	);

	const confirmButtonActual = overrrideProps(confirmButton, 'confirm') ?? (
		<Button
			variant={renderConfirmButtonVariant()}
			size="lg"
			className="h-auto min-h-9 w-full whitespace-normal"
			data-testid={testIds?.confirm ?? 'confirm-alert-confirm'}
			onClick={() => {
				onConfirm();
				setIsOpen(false);
			}}
		>
			{confirmButtonText ?? (type === 'delete' ? t`Yes, delete` : t`Confirm`)}
		</Button>
	);

	return (
		<AlertDialog open={isOpen} onOpenChange={setIsOpen}>
			<AlertDialogContent
				data-testid={testIds?.dialog}
				className="w-[90%] md:min-w-[450px] md:w-[450px] rounded-xl bg-background p-6 z-[9999]"
			>
				<Button
					variant="link"
					size="icon"
					className="absolute top-1 right-1 text-muted-foreground"
					onClick={() => onCancel()}
				>
					<X className="w-4 h-4" />
				</Button>
				<AlertDialogHeader className="flex flex-col gap-2 items-center">
					{type === 'delete' && (
						<div className="w-10 h-10 rounded-md bg-background flex items-center justify-center border border-border">
							<Trash className="size-5 text-destructive" />
						</div>
					)}
					{isDestructiveStyled && (
						<div className="w-10 h-10 rounded-md bg-destructive/10 flex items-center justify-center border border-destructive/40">
							<AlertOctagon className="size-5 text-destructive" />
						</div>
					)}
					<AlertDialogTitle
						className={cn(
							'text-2xl font-semibold max-w-full [overflow-wrap:anywhere]',
							isDestructiveStyled ? 'text-destructive' : 'text-foreground',
						)}
					>
						{title}
					</AlertDialogTitle>
					<AlertDialogDescription className="text-left text-base text-muted-foreground font-normal max-w-full [overflow-wrap:anywhere]">
						{description}
					</AlertDialogDescription>
					{expiresAt !== undefined && <RequestCountdown expiresAt={expiresAt} />}
				</AlertDialogHeader>
				<AlertDialogFooter
					className={cn(
						'gap-3 bg-background border-none',
						// On a phone the buttons stack, the action on top, so a long label never runs out of its button.
						hideCancelButton ? 'grid grid-cols-1' : 'flex flex-col-reverse sm:grid sm:grid-cols-2 sm:gap-4',
					)}
				>
					{!hideCancelButton && cancelButtonActual}
					{confirmButtonActual}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

interface PendingConfirmAlert {
	props: BaseConfirmAlertProps;
	/** Location the alert was raised from, so only a real navigation away dismisses it. */
	openedAtLocation: string;
}

export const GlobalConfirmAlert = () => {
	const [pendingAlert, setPendingAlert] = useState<PendingConfirmAlert | null>(null);
	const currentLocation = useRouterState({ select: (state) => state.location.href });

	useEvent('SHOW_CONFIRM_ALERT', (props) => {
		setPendingAlert({ props, openedAtLocation: currentLocation });
	});

	// The alert lives at the app root, outside the tree that raised it, so a navigation
	// would otherwise orphan it: visible with no context and with its promise never settling.
	// Cancel is the only safe direction - confirm would run a destructive action nobody approved.
	useEffect(() => {
		if (!pendingAlert || pendingAlert.openedAtLocation === currentLocation) return;
		pendingAlert.props.onCancel();
		setPendingAlert(null);
	}, [pendingAlert, currentLocation]);

	if (!pendingAlert) return null;

	return (
		<ConfirmAlert
			{...pendingAlert.props}
			onCancel={() => {
				pendingAlert.props.onCancel();
				setPendingAlert(null);
			}}
			onConfirm={() => {
				pendingAlert.props.onConfirm();
				setPendingAlert(null);
			}}
			onTimeout={() => {
				pendingAlert.props.onTimeout?.();
				setPendingAlert(null);
			}}
		/>
	);
};
